import { SolutionPage } from "@/components/shared/SolutionPage";
import { PhoneSystemMockup } from "@/components/shared/PhoneSystemMockup";
import { Phone, Bot, Network, TrendingUp } from 'lucide-react';
import { Link } from "react-router-dom";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

/**
 * Capability spoke: "Business Phone System".
 * Single-intent page for "business phone system" / "VoIP for small business" /
 * "business phone lines" demand. Hub is /platform; clustered to its parent
 * layers /solutions/crm-automation (calls + texts live in the CRM) +
 * /solutions/ai-strategy-rollout (the AI answers on these lines).
 * Positioned as the small-business alternative to a traditional carrier line.
 * Expanded 2026-07-18 (phones-full-system branch) to the full Frayze Phones
 * offer: hardware supplied/installed (desk phones, paging/intercom, switches,
 * wiring), full PBX features (auto attendant, ring groups, park), all-in
 * plans from $24.95/user (DRAFT pricing, pending platform terms; verify
 * before merge), and a BBAA grant FAQ worded per the grant-messaging rule.
 * Pricing stated is Frayze's own offer. No competitor prices invented.
 * No geography in SEO targeting (cluster ownership rule).
 */
const BusinessPhoneSystem = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Business Phone System"
      seo={{
        title: "AI-Enabled Business Phone System, Installed | Frayze",
        description: "A complete business phone system with AI built in: desk phones supplied and installed, auto attendant, paging, mobile apps, and every call logged in your CRM. Lines from $2/month plus usage, or all-in plans from $24.95 per user. The small-business alternative to a traditional carrier line.",
        keywords: "business phone system, voip for small business, business phone lines, voip phone system, office phone system installation, desk phones for business, overhead paging system, sip trunking, call recording business phone, text enabled business number, ai phone system, business voip",
        canonical: "https://frayze.ca/platform/business-phone-system/"
      }}
      hero={{
        badge: "Capability, Communications",
        badgeColor: "text-sky-700",
        title: "A Business Phone System With a Brain, Not Just a Dial Tone.",
        description: "Desk phones on the desks, an auto attendant on the line, paging in the shop, apps on every pocket, and an AI that answers when you can't. We supply the phones, run the wiring, and install the whole system. Every call and text lands in your CRM, not in a voicemail box nobody checks.",
        buttonColor: "bg-sky-700 hover:bg-sky-800",
        gradientParams: "from-sky-500/5 via-background to-background",
        primaryCta: { text: "Price Out My Lines", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "See Pricing", link: "/pricing/" },
        visual: <PhoneSystemMockup />
      }}
      problem={{
        title: "You're Paying Big-Carrier Prices for a Line That Just Rings",
        description: (
          <>
            A traditional business line does one thing: ring. It doesn't record the call, doesn't take a text, doesn't know who's calling, and doesn't remember the conversation. Miss the call and the lead is gone.
            <br /><br />
            <strong>What a "dumb" line actually costs you:</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "Just rings", label: "No recording, no texting, no caller history, no AI. The line does nothing but ring, and voicemail is where leads go to die" },
          { value: "À la carte", label: "Traditional carriers price per line and per add-on. The useful features cost extra when they're offered at all" },
          { value: "No memory", label: "When the call ends, it's gone. No transcript, no record in a CRM, no automatic follow-up. Just a number on a bill" }
        ]
      }}
      systemModel={{
        title: "How the Frayze Phone System Works",
        description: "Your number becomes a smart front door: answered, recorded, and remembered.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-sky-500/20 via-sky-500/50 to-sky-500/20",
        items: [
          { step: "1", title: "Keep Your Number, Get Real Phones", description: "We port your existing business number in, or set up new local lines. We supply and install the desk phones, cordless handsets, network gear, and wiring, and phones arrive pre-configured: plug in and dial." },
          { step: "2", title: "Every Call Gets Answered", description: "Your team answers on desk phone, mobile app, or browser. Busy or closed? The AI receptionist picks up, and missed calls get an instant text-back." },
          { step: "3", title: "Recorded and Logged", description: "Calls are recorded and attached to the caller's contact record automatically: who called, what was said, what happens next." },
          { step: "4", title: "Followed Up From the CRM", description: "Calls, texts, and voicemails land in one inbox tied to your pipeline, so every conversation turns into a tracked follow-up instead of a sticky note." }
        ]
      }}
      layers={{
        title: "What Comes With the Phone System",
        description: "Real phone infrastructure plus the intelligence layer traditional carriers don't sell.",
        theme: "indigo",
        items: [
          {
            title: "The Phone System",
            icon: Phone,
            items: [
              "Lines from $2/month plus usage, or all-in plans from $24.95 per user with unlimited Canada calling",
              "Auto attendant, ring groups, transfer, park, and voicemail to email",
              "Call recording and text-enabled business numbers",
              "Mobile and desktop apps, number porting included"
            ]
          },
          {
            title: "Hardware & Install",
            icon: Network,
            items: [
              "Desk phones, cordless handsets, and conference phones, supplied pre-configured (buy or lease)",
              "Overhead paging and intercom zones for shops, service bays, and warehouses",
              "Network switches and wiring installed and tested on site",
              "Local install and support, not a courier box and a help line"
            ]
          },
          {
            title: "The Intelligence Layer",
            icon: Bot,
            items: [
              "AI receptionist answers when you can't",
              "Missed-call text-back built in",
              "Every call and text logged to the caller's CRM record",
              "One inbox for calls, texts, and voicemail"
            ]
          }
        ]
      }}
      interdependence={{
        title: "The Phone Line Is Where the Lead System Starts",
        description: (
          <>
            For a local business, the phone is the highest-intent channel there is, which is why it shouldn't be a dumb pipe. Every call and text flows into the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM that remembers every customer</Link>, and the <Link to="/platform/ai-receptionist/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">AI receptionist</Link> from the <Link to="/solutions/ai-strategy-rollout/" className="font-bold underline decoration-violet-500/50 hover:text-violet-600 transition-colors">Intelligence System</Link> answers on these same lines when you can't.
            <br /><br />
            See where it sits in the wider <Link to="/platform/" className="font-bold underline decoration-sky-500/50 hover:text-sky-600 transition-colors">managed platform</Link>.
          </>
        ),
        icon: Network,
        quote: "The most expensive phone line is the cheap one that loses the call."
      }}
      useCases={{
        title: "Who the Phone System Fits Best",
        items: [
          { title: "Car Dealerships", description: "Sales, service, and BDC lines with every call recorded and logged to the deal. No appraisal, follow-up, or be-back gets lost on a handset.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "One number that rings the crew, texts the customer, and books the estimate when you're on a roof and can't pick up.", link: "/industries/trades/" },
          { title: "Any Multi-Line Office", description: "Clinics, shops, and offices paying per-line carrier rates for features a modern system includes by default.", link: "/solutions/crm-automation/" }
        ]
      }}
      socialProof={{
        title: "Built by an operator who ran dealership phone volume",
        quote: "Before Frayze, Denis ran the communication systems behind a six-rooftop dealership group. Every sales and service call was tracked, recorded, and worked in the CRM instead of dying in voicemail. That phone discipline is what Frayze installs for you.",
        icon: TrendingUp,
        ctaText: "See the Need A Car story",
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "Stop Renting a Dial Tone. Get a Phone System That Works the Lead.",
        description: "Phones on the desks, paging in the shop, AI on the line. Installed locally, priced like a phone plan, and connected to your CRM. It starts with a free 20-minute walkthrough of your current phones and internet.",
        primaryCta: { text: "Price Out My Lines", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "Get a Free Phone-System Audit", link: "/digital-audit/" },
        color: "bg-sky-700 hover:bg-sky-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          <ServiceFAQ
            title="Business Phone System: Common Questions"
            description="Straight answers on lines, pricing, deskphones, and what happens to your current number."
            items={[
              {
                question: "What is the Frayze business phone system?",
                answer: "It is a modern VoIP phone system built into the Frayze platform. You get real business lines from $2 per month plus usage, with call recording, text-enabled numbers, and deskphone or SIP trunking support. Unlike a traditional line, every call and text is logged against the caller's record in your CRM, with an AI receptionist backstop for the calls you miss."
              },
              {
                question: "Can this replace my Tbaytel, Rogers, or Bell business line?",
                answer: "For most small businesses, yes. We port your existing number in so nothing changes for your customers, your existing desk phones can connect over SIP, and the line does far more than the one it replaces (recording, texting, CRM logging, and AI answering). Your customers just see the same number; you see every conversation."
              },
              {
                question: "Is it really $2 a line? What's the catch?",
                answer: "Lines start at $2 per month, and then calling and texting are billed as usage (the same way a phone plan works). For teams that prefer a flat bill, all-in plans start at $24.95 per user per month with unlimited Canada calling. There is no per-feature ladder: recording, texting, and CRM logging are part of the system, not add-ons. Usage is metered transparently, and we size it with you up front so there are no surprises on the bill."
              },
              {
                question: "Do you supply and install the desk phones and wiring?",
                answer: "Yes. We supply desk phones, cordless handsets, and conference phones, available for purchase or on a monthly lease, and they arrive pre-configured so setup is plug in and dial. Where a site needs network drops, a power-over-ethernet switch, or new wiring, we install and test it ourselves. You get one local team for the phones, the network, and the software, not three vendors pointing at each other."
              },
              {
                question: "Can you do overhead paging and intercom?",
                answer: "Yes. We install IP paging speakers and intercom zones for shops, service bays, warehouses, and kitchens, tied into the same phone system. One-touch page from any desk phone, zone by zone, plus scheduled bells or announcements where they are useful. Existing analog overhead amplifiers can usually be connected rather than replaced."
              },
              {
                question: "Does it have real office features like an auto attendant and ring groups?",
                answer: "Yes. Auto attendant menus, ring groups, call transfer, call park, business-hours routing, and voicemail to email are all standard. The difference from a traditional office system is what sits on top: the AI receptionist, missed-call text-back, and CRM logging that turn the phone system into a lead system."
              },
              {
                question: "Is there funding available for the AI side of this?",
                answer: "Possibly. Northern Ontario businesses may qualify for the Building Blueprints for AI Adoption program, a regional grant that can reimburse 50 percent of eligible AI adoption project costs up to $20,000. Eligibility is decided by the program, not by us, and applications are made by your business with our documentation support. Ask us and we will help you check."
              },
              {
                question: "Do desk phones still work, or is this app-only?",
                answer: "Both. Your team can answer on a mobile app, in the browser, or on physical desk phones connected through SIP trunking. If you have handsets you like, we can usually keep them; if you want to go app-only, you can skip the hardware entirely."
              },
              {
                question: "Can customers text my business number?",
                answer: "Yes. Your lines are text-enabled, so customers can text the same number they call, and your team replies from one inbox with the full history attached. We handle the carrier registration required for business texting, and every outbound message carries your business identity and an opt-out, keeping you onside with Canadian anti-spam rules."
              },
              {
                question: "What happens to my current number when I switch?",
                answer: "We port it. Number porting is a standard, regulated process. Your number moves onto the new system and keeps working through the transition, so you never have to reprint a truck wrap, a sign, or a business card. New local numbers are available too if you want dedicated lines for tracking."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default BusinessPhoneSystem;
